import React from 'react';
import { randomId } from '@aha-app/mvc';
import type { WhiteboardController } from '@aha-app/whiteboard';
import type { RecordId } from 'javascripts/shared-types';
import type AssistantViewController from 'components/Assistant/components/AssistantView/AssistantViewController';
import { type ScopeItem } from 'components/Assistant/components/AssistantView/AssistantViewController';
import { SelectionScopeItem } from 'components/Assistant/components/PromptPanel/ScopeItems';

type DataURL = `data:${string}`;

class AssistedWhiteboard {
  selectionScopeItem: ScopeItem;
  private versionSavedForSession: boolean = false;

  constructor(private readonly whiteboardController: WhiteboardController) {}

  // Used to ensure a version is saved before AI makes destructive changes
  public ensureVersionSaved(): void {
    const hasContent = this.whiteboardController.getContentSummary();
    const { historyManager } = this.whiteboardController;

    if (hasContent && historyManager && !this.versionSavedForSession) {
      void historyManager.actionSaveVersion();
      this.versionSavedForSession = true;
      this.whiteboardController.actionShowMessage(
        'Saved whiteboard version before edits'
      );
    }
  }

  // Pass-through methods to expose only specific functionality to AI tools
  actionProcessOperations: typeof this.whiteboardController.actionProcessOperations =
    (...args) => this.whiteboardController.actionProcessOperations(...args);

  getContentSummary() {
    return this.whiteboardController.getContentSummary();
  }

  getWhiteboardJSON() {
    return this.whiteboardController.whiteboardContentAsJSON();
  }

  actionAddGeneratedWireframe(wireframe: string): void {
    this.whiteboardController.actionAddGeneratedWireframe(wireframe);
  }

  actionAddDiagram(diagram: string, opts?: { animate?: boolean }): void {
    void this.whiteboardController.actionAddDiagram(diagram, opts);
  }

  async actionPlaceAssistantArtifact({
    token,
    version,
  }: {
    token: string;
    version?: string;
  }): Promise<void> {
    await this.whiteboardController.actionPlaceAssistantArtifact({
      token,
      version,
    });
  }

  getSelection() {
    return this.whiteboardController.getWhiteboardSelectionForAssistant();
  }

  // Called multiple times - every time a chat is instantiated
  attachToAssistantViewController(
    assistantViewController: AssistantViewController
  ): void {
    // Set up scope item for this chat - this links the selection to the active
    // chat session in the assistant side panel
    const proxiedSelectionScopeItem = assistantViewController.setScopeItem(
      'whiteboardSelection',
      {
        id: randomId() as RecordId,
        resolve: () => this.getSelection(),
        render: null,
      }
    );
    this.selectionScopeItem = proxiedSelectionScopeItem;

    assistantViewController.setScopeItem('colorPalette', {
      id: randomId() as RecordId,
      resolve: () => this.whiteboardController.getCurrentColorPalette(),
      render: null,
    });
  }

  registerSelectionChangeListener() {
    this.whiteboardController.registerCanvasSelectionChangeListener(objects => {
      if (!this.selectionScopeItem) return;

      if (objects.length === 0) {
        this.selectionScopeItem.render = null;
        return;
      }

      this.selectionScopeItem.render = () => {
        return (
          <SelectionScopeItem
            content={`${objects.length} ${
              objects.length === 1 ? 'item' : 'items'
            } selected`}
          />
        );
      };
    });
  }

  async actionCreateImageShape(imageSrc: DataURL, file: File, options = {}) {
    const placeAtPoint = this.whiteboardController.canvas.getVpCenter();

    await this.whiteboardController.actionCreateImageShape(
      imageSrc,
      placeAtPoint,
      file,
      options
    );
  }
}

export default AssistedWhiteboard;
